const toDeterministicSeed = (...parts) =>
  parts
    .join('::')
    .split('')
    .reduce((sum, char, index) => sum + (char.charCodeAt(0) * (index + 1)), 0);

const shuffleWithSeed = (items = [], seed) => {
  const arr = [...(items || [])];
  let state = Number(seed) || 0;
  const nextRand = () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(nextRand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const toStoreScope = (storeId) => `store:${String(storeId)}`;

export const orderProductsForComplianceDisplay = (products, storeId, taskType, dateKey) => {
  if (!Array.isArray(products) || !products.length || storeId == null) {
    return products || [];
  }
  if (products.some((product) => product.priority_rank !== undefined || product.priorityRank !== undefined)) {
    return products;
  }
  const storeScope = toStoreScope(storeId);
  return shuffleWithSeed(
    products,
    toDeterministicSeed(storeScope, taskType, dateKey, 'display-order')
  );
};

export const orderAssignmentBundleForDisplay = (assignments, storeId, dateKey) => ({
  dateCheck: orderProductsForComplianceDisplay(assignments.dateCheck, storeId, 'date_check', dateKey),
  shortlifeDateCheck: orderProductsForComplianceDisplay(
    assignments.shortlifeDateCheck,
    storeId,
    'short_life',
    dateKey
  ),
  dailyInventory: orderProductsForComplianceDisplay(assignments.dailyInventory, storeId, 'inventory', dateKey),
  produceDateCheck: orderProductsForComplianceDisplay(assignments.produceDateCheck, storeId, 'produce', dateKey),
  temperatureCheck: assignments.temperatureCheck || [],
});

export const normalizeAssignmentProduct = (product = {}) => {
  const id = String(product.id || product.product_id || product.sku || '').trim();
  const sku = String(product.sku || product.SKU || product.product_id || '').trim();
  const name = String(
    product.name
    || product.product_name
    || product.assetName
    || product.asset_name
    || product.product
    || product.Name
    || sku
  ).trim();

  return {
    ...product,
    id,
    sku,
    name,
  };
};

export const normalizeAssignmentBundle = (assignments = {}) => ({
  dateCheck: (assignments.dateCheck || []).map(normalizeAssignmentProduct).filter((p) => p.id),
  shortlifeDateCheck: (assignments.shortlifeDateCheck || []).map(normalizeAssignmentProduct).filter((p) => p.id),
  dailyInventory: (assignments.dailyInventory || []).map(normalizeAssignmentProduct).filter((p) => p.id),
  produceDateCheck: (assignments.produceDateCheck || []).map(normalizeAssignmentProduct).filter((p) => p.id),
  temperatureCheck: (assignments.temperatureCheck || []).map(normalizeAssignmentProduct).filter((p) => p.id),
});

export const buildExpectedSubtaskMap = (assignments = {}) => ({
  dateCheck: (assignments.dateCheck || []).map((product) => String(product.id)),
  shortlifeDateCheck: (assignments.shortlifeDateCheck || []).map((product) => String(product.id)),
  dailyInventory: (assignments.dailyInventory || []).map((product) => String(product.id)),
  produceDateCheck: (assignments.produceDateCheck || []).map((product) => String(product.id)),
  temperatureCheck: (assignments.temperatureCheck || []).map((asset) => String(asset.id)),
});

export const applyComplianceSummaryToTasks = (summary, dailyTasks) => {
  const tasks = summary?.tasks || {};
  const completedTasks = Object.fromEntries(
    dailyTasks.map((task) => [task.key, !!tasks[task.key]?.completed])
  );
  return { taskStats: tasks, completedTasks };
};
